import { ReactNode } from 'react';
import * as React from 'react';
import { Href } from 'redux-little-router';

import { Shape2Icon, Shape2Tone } from '../components/Shape2/Shape2';

import { BannerId } from './banners';
import { EditBannersSessionValue, MassActionId } from './bannersEditState';
import { WalletDayBudgetValue } from './budget';
import { EditCampaignSessionValue } from './campaignsEdit';
import { CampaignsCalcTypeFilter, FilterGridViewDict } from './domain';
import { PeriodFilterValue } from './filters';
import { GoalDecl } from './goals';
import {
    CommonQuery_client_access,
    CommonQuery_operator_access,
    CurrencyCode,
    GdGeotreeType,
    GdiRecommendationType,
    GdWalletAction,
    StatusModerateBannerPage
} from './graphql-grid';
import { GridEditedEntityType, GridScreenTotalKey } from './grid';
import { GroupId } from './groups';
import { EditGroupSessionValue } from './groupsEditState';
import { AgencyInfo, CommonState, ManagerInfo } from './state';

export const GRID_PREFIX = 'grid';
export const LOG_PREFIX = 'log';
export const APP_PREFIX = 'mobile-apps';
export const APPS_EDIT_PREFIX = 'edit';
export const RECOMMENDATIONS_PREFIX = 'recommendations';
export const RECOMMENDATIONS_GRID_PREFIX = 'recommendations-grid';
export const FREELANCERS_PREFIX = 'freelancers';
export const CUSTOMERS_PREFIX = 'customers';
export const BANNERS_EDIT_PREFIX = 'banners-edit';
export const INTERNAL_BANNERS_EDIT_PREFIX = 'internal-banners-edit';
export const CAMPAIGNS_EDIT_PREFIX = 'campaigns-edit';
export const GROUPS_EDIT_PREFIX = 'groups-edit';
export const DEALS_PREFIX = 'deals';
export const TOOLS_PREFIX = 'tools';
export const LIBRARY_PREFIX = 'library';
export const MINUS_PHRASE_PREFIX = 'minusPhrase';

export const logs = 'logs';
export const reportsClient = 'reportsClient';
export const reportsDomain = 'reportsDomain';
export const clients = 'clients';
export const campaigns = 'campaigns';
export const groups = 'groups';
export const banners = 'banners';
export const phrases = 'phrases';
export const usersProfile = 'usersProfile';
export const feedFilters = 'feedFilters';
export const retargetings = 'retargetings';
export const audiences = 'audiences';
export const deals = 'deals';
export const minusPhraseLibrary = 'minusPhraseLibrary';

export interface ClientInfo {
    hasEcommerce: boolean;
    hasMetrikaCounters: boolean;
    hasCampaignsWithCPAStrategy: boolean;
    isWalletEnabled: boolean;
    isBrandLiftAllowed: boolean;
    enabledRecommendationTypes?: Array<GdiRecommendationType>;
    workCurrency: CurrencyCode;
    isBannerUpdateAllowed: boolean;
    isCpmBannerUpdateAllowed: boolean;
    isAdGroupUpdateAllowed: boolean;
    countryRegionId: string;
    isMcBannerDnaEnabled: boolean;
    isBannerPricesOnGridEnabled?: boolean;
    isInternalAdsAllowed?: boolean;
    isTycoonOrganizationsEnabled?: boolean;
}

export type clients = typeof clients;
export type campaigns = typeof campaigns;
export type groups = typeof groups;
export type banners = typeof banners;
export type phrases = typeof phrases;
export type usersProfile = typeof usersProfile;
export type feedFilters = typeof feedFilters;
export type retargetings = typeof retargetings;
export type audiences = typeof audiences;
export type logs = typeof logs;
export type deals = typeof deals;
export type reportsClient = typeof reportsClient;
export type reportsDomain = typeof reportsDomain;
export type minusPhraseLibrary = typeof minusPhraseLibrary;

export enum Lang {
    ru = 'ru',
    en = 'en',
    uk = 'uk',
    tr = 'tr'
}

export enum Tld {
    ru = 'ru',
    by = 'by',
    ua = 'ua',
    com = 'com',
    comTr = 'com.tr',
    kz = 'kz'
}

export enum TimeTargetingDay {
    monday = 'monday',
    tuesday = 'tuesday',
    wednesday = 'wednesday',
    thursday = 'thursday',
    friday = 'friday',
    saturday = 'saturday',
    sunday = 'sunday'
}

export interface HeaderTabsData {
    key: string;
    title?: React.ReactNode | React.ReactNode[];
    getTitle?: (
        onRecommendationTooltipClose: () => void
    ) => React.ReactNode | React.ReactNode[];
    location?: Href;
    isExternal?: boolean;
    target?: LinkTarget;
    isActive?: boolean;
    group?: Array<HeaderTabsData>;
    description?: string;
    icon?: Shape2Icon;
    isAvailable?: boolean;
    onClick?: () => void;
}

export interface NavLinkData {
    title?: React.ReactNode | React.ReactNode[];
    getTitle?: (
        onRecommendationTooltipClose: () => void
    ) => React.ReactNode | React.ReactNode[];
    location?: Href;
    isExternal?: boolean;
    target?: LinkTarget;
    isActive?: boolean;
    group?: Array<NavLinkData>;
    description?: string;
    icon?: Shape2Icon;
    onClick?: () => void;
}

export enum DateRangePreset {
    today = 'today',
    yesterday = 'yesterday',
    currentWeek = 'currentWeek',
    previousWeek = 'previousWeek',
    last7Days = 'last7Days',
    currentMonth = 'currentMonth',
    previousMonth = 'previousMonth',
    last30Days = 'last30Days',
    last90Days = 'last90Days',
    last365Days = 'last365Days',
    allTime = 'allTime',
    default = 'last30Days',
    period = 'period'
}

export enum LinkTarget {
    blank = '_blank',
    self = '_self'
}

export enum ValidationErrorType {
    nan = 'nan',
    lte = 'lte',
    gte = 'gte',
    empty = 'empty',
    server = 'server'
}

export interface ValidationResult {
    valid: boolean;
    errors?: Array<{
        type: string;
        text: string | (string | number | object | undefined)[];
        path: string;
    }>;
}

export type FormRequestParamsData = {
    [index: string]: string | (string[]);
};

export enum ErrorResponseType {
    graphQL = 'graphQL',
    network = 'network',
    internal = 'internal'
}

export interface FormRequestParams {
    action: string;
    method?: 'POST' | 'GET';
    target?: LinkTarget;
    data?: FormRequestParamsData;
}

export enum KeyCode {
    RETURN = 13,
    ESC = 27,
    BACKSPACE = 8,
    UP = 38,
    DOWN = 40,
    SHIFT = 16,
    CTRL = 17
}

export interface Session {
    editBanners?: EditBannersSessionValue;
    campaignsIds?: Array<string>;
    groupsIds?: Array<string>;
    gridFilter?: FilterGridViewDict;
    order?: number;
    statPeriod?: PeriodFilterValue;
    editCampaigns?: EditCampaignSessionValue;
    editGroups?: EditGroupSessionValue;
    goalsSettings?: GoalDecl[];
    alertMessageFilter?: FilterGridViewDict;
    campaignsCalcTypeFilter?: CampaignsCalcTypeFilter;
}

export type SessionDict = Record<string, Session>;

export interface DropdownChooserItem {
    id: string;
    addition?: string;
    name: string;
    nameCls?: string;
    isDisabled?: boolean;
}

export interface ColumnsSettingsViewDict {
    [key: string]: ColumnsSettings;
}

export interface ColumnsSettings {
    columnOrder: Array<string>;
    columnViewInfo: ColumnViewInfo;
}

export interface ColumnViewInfo {
    [key: string]: ColumnTypesSettings;
}

export interface ColumnTypesSettings {
    width: number;
    widthCompact?: number;
    hidden?: boolean;
    totalKey?: GridScreenTotalKey;
}

export interface CampaignSaveSuccessData {
    type: GridEditedEntityType.Campaign;
    cid: string;
}

export interface GroupSaveSuccessData {
    type: GridEditedEntityType.Group;
    adgroupId: string;
}

export type EntitySaveSuccessData =
    | CampaignSaveSuccessData
    | GroupSaveSuccessData;

export interface CellKeyFields {
    id: string;
    column?: string;
    view: string;
}

export type ValidationResultItemParams = object | string | null;

export interface ValidationResultItemParamsPixel {
    accessibleClientProviderNames?: string[];
    exceededClientPixelProviderNames?: string[];
    pixelUrl?: string;
}

export interface ValidationResultItem {
    code: string;
    params: ValidationResultItemParams;
    text?: string | React.ReactNode;
    value?: string;
    path: string;
}

export type ValidationErrorsSet = Record<string, ValidationResultItem[]>;

export type CellKeyFieldsPartial = Pick<
    CellKeyFields,
    Exclude<keyof CellKeyFields, 'view'>
>;

export interface FooterData {
    manager: {
        name: string;
        tel?: string;
        email: string;
        representativeName?: string;
    };
    contacts: Array<
        | {
              name: string;
              tel?: string;
          }
        | undefined
    >;
}

type BackendAccessRights = Exclude<CommonQuery_client_access, '__typename'> &
    Exclude<CommonQuery_operator_access, '__typename'>;

export type AccessRights = Partial<
    Exclude<BackendAccessRights, 'canCreateCampaignTypes'>
> & { canCreateCampaignTypes: BackendAccessRights['canCreateCampaignTypes'] };

export type CommonData = Pick<
    CommonState,
    | 'constants'
    | 'availableCampaignTypes'
    | 'accessRights'
    | 'wallet'
    | 'operator'
    | 'client'
    | 'features'
>;

export interface BasePopupProps {
    switchPopupState: () => void;
    closePopup: () => void;
    openPopup: () => void;
    isPopupVisible: boolean;
}

export interface OperatorModerationInfo {
    name: string;
    statusModerate: StatusModerateBannerPage;
}

export type SaveSuccessData = CampaignSaveSuccessData | GroupSaveSuccessData;

export interface AuthorizationData {
    id: string;
    login: string;
    isFreelancer: boolean;
    userId: string;
}

export interface QueryAdditionalData {
    reqId: string;
}

export interface TranslateBrandLift {
    title: string;
    text: string;
}

export interface IconBrandLift {
    icon: Shape2Icon;
    tone: Shape2Tone;
}

export interface WalletData {
    id?: string;
    sum?: number;
    currency?: CurrencyCode;
    walletBudget?: WalletDayBudgetValue;
    walletActions: Array<GdWalletAction>;
}

export interface UserAccountInfo {
    uid: string;
    login: string;
    displayName: {
        name: string;
        avatar: string;
    };
    defaultEmail: string;
}

export type UserAccountsInfo = {
    data?: Array<UserAccountInfo>;
    current?: UserAccountInfo;
    error?: boolean;
};

export interface OfficeApiContact {
    description: string;
    cityName: string | null;
    phone: string;
    phoneExtension: string | null;
}

export interface OfficeContact {
    description: string;
    cityName?: string;
    phone: string;
    phoneExtension?: string;
}

export interface OfficeInfo {
    cityContact?: OfficeContact;
    regionContacts: OfficeContact[];
}

export interface OperatorData {
    tuneSecretKey?: string;
    officeInfo: OfficeInfo;
    email?: string;
    isAgency: boolean;
    isSelfAgency: boolean;
    login: string;
}

// ClientData - cуществующий тип
export interface ClientProperties {
    ClientID?: string;
    workCurrency: CurrencyCode;
    login?: string;
    countryRegionId?: string;
    managersInfo?: ManagerInfo[];
    agencyInfo?: AgencyInfo;
    translocalGeotreeType?: GdGeotreeType;
    autoVideo: boolean;
}

export enum PageStatus {
    LOADING = 'loading',
    LOADED = 'loaded',
    ERROR = 'error',
    SAVING = 'saving'
}

export interface ValidationErrorData {
    code?: string;
    path?: string;
    text: ReactNode;
}

export enum ThemePopup {
    DEFUALT = 'default',
    CLEAR = 'clear-body',
    FULL = 'full-width-body'
}

export type ValidationErrorsData = Record<
    string,
    ErrorsForValidationErrorsData
>;

export interface ErrorsForValidationErrorsData {
    errors?: ValidationErrorData[];
}

export type ErrorsEdit = {
    groups?: Record<GroupId, ValidationErrorsData>;
    banners?: Record<
        BannerId,
        ValidationErrorsData | ErrorsForValidationErrorsData
    >;
    common?: ErrorsForValidationErrorsData;
    sitelinks?: ErrorsForValidationErrorsData;
};

export enum TurboTemplate {
    EMPTY = 'empty-preset',
    SHORT = 'short-preset',
    SHOP = 'commodity-preset',
    LIBRARY = 'library',
    METRO = 'cpm-geoproduct-preset'
}

export type FlattenRegions = Record<string, Region>;
export type RegionId = number;

export interface Region {
    id: string;
    name: string;
    inner: Array<Region> | null;
}

export interface RegionInfo {
    id: string;
    name: string;
}

export interface RegionSelectedInfo {
    region: RegionInfo;
    excepts: Array<RegionInfo>;
}

export interface DirectInputTextField {
    count?: number;
    text: string;
    valid: boolean;
    errors?: ValidationErrorData[];
}

export interface OfficeApiContacts {
    support?: OfficeSupportApiData;
    client?: OfficeSupportApiData;
    email: string;
}

export interface OfficeSupportApiData {
    landline?: [[string, number, string]];
    regional?: [[string, number, string]];
}

export enum TuplePosition {
    start = 'start',
    end = 'end'
}

export type NumberRangeInputValues = [number?, number?];

export interface CommonSettingEditor {
    isGridTotalBoxVisible: boolean;
    isCompactViewEnabled: boolean;
    isOnlyFavoriteCampaignsVisible: boolean;
}

export interface CommonSettingEditorDiff {
    isGridTotalBoxVisible?: boolean;
    isCompactViewEnabled?: boolean;
    isOnlyFavoriteCampaignsVisible?: boolean;
}

export type EditorPlaceId = BannerId | MassActionId;

export interface DictionaryItem {
    val: string;
    text: string;
}

export interface NumberInputField {
    inputVal: string;
    numVal?: number;
    error?: string;
}

export type Size = {
    width: number;
    height: number;
};
